import {
  type Candidate,
  type CandidateFilters,
  type CandidatePage,
  type CandidatePageQuery,
} from "@vidario/domain";
import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";

import { useVidarioClient } from "../client";
import { ApiError } from "../endpoints";
import { queryKeys } from "../queryKeys";

export function useCandidates(
  jobId: string,
  filters?: CandidateFilters,
): UseQueryResult<Candidate[]> {
  const client = useVidarioClient();
  return useQuery({
    queryKey: queryKeys.candidates(jobId, filters),
    queryFn: () => client.getCandidates(jobId, filters),
    enabled: Boolean(jobId),
  });
}

const CANDIDATE_PAGE_RETRIES = 1;

/** One page of the candidate page (#14). While the next page or filter loads,
 * the current page stays on screen (`isPlaceholderData`), so the list updates
 * in place instead of flashing a loading state. */
export function useCandidatePage(
  jobId: string,
  query: CandidatePageQuery,
): UseQueryResult<CandidatePage> {
  const client = useVidarioClient();
  return useQuery({
    queryKey: queryKeys.candidatePage(jobId, query),
    queryFn: () => client.getCandidatePage(jobId, query),
    enabled: Boolean(jobId),
    placeholderData: keepPreviousData,
    // A 4xx (an invalid filter) won't fix itself; anything else gets one more try
    // before the page shows the error.
    retry: (failures, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) &&
      failures < CANDIDATE_PAGE_RETRIES,
  });
}

export function useCandidate(jobId: string, candidateId: string): UseQueryResult<Candidate> {
  const client = useVidarioClient();
  return useQuery({
    queryKey: queryKeys.candidate(jobId, candidateId),
    queryFn: () => client.getCandidate(jobId, candidateId),
    enabled: Boolean(jobId) && Boolean(candidateId),
  });
}
